import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '../core/hooks/useAuth';
import { Cloud, KeyRound, AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useInstance } from '../core/contexts/InstanceContext';

export default function Login() {
  const { login, loginLocal } = useAuth();
  const instance = useInstance();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [showLocalLogin, setShowLocalLogin] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check for OIDC callback
  const code = searchParams.get('code');
  if (code) {
    // This is handled by the backend callback
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Anmeldung wird verarbeitet...</p>
        </div>
      </div>
    );
  }

  const handleOIDCLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await login();
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'OIDC not configured') {
        setShowLocalLogin(true);
        setError('Nextcloud SSO ist nicht konfiguriert. Bitte lokalen Admin-Login verwenden.');
      } else {
        setError('Anmeldung fehlgeschlagen');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLocalLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await loginLocal(username, password);
      toast.success('Erfolgreich angemeldet');
      navigate('/');
    } catch (err: unknown) {
      console.error('LOGIN ERROR:', err);
      setError('Ungültige Anmeldedaten');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-gray-50 to-gray-100 p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <img
            src="/api/instance/assets/logo"
            alt={instance.name.short}
            width={360}
            height={135}
            className="mx-auto mb-16"
          />
          <h1 className="text-2xl font-bold text-charcoal">{instance.branding.subtitle}</h1>
          <p className="text-gray-500 mt-2">Bitte melden Sie sich an</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
          {/* Error message */}
          {error && (
            <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 text-red-700 rounded-lg">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* OIDC Login */}
          {!showLocalLogin && (
            <>
              <button
                onClick={handleOIDCLogin}
                disabled={isLoading}
                className="btn-primary w-full py-3 text-lg flex items-center justify-center gap-3"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Cloud className="w-5 h-5" />
                )}
                <span>Mit Nextcloud anmelden</span>
              </button>

              {/* Divider - only shown when OIDC login is visible */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-400">oder</span>
                </div>
              </div>
            </>
          )}

          {/* Local Admin Login Toggle */}
          {!showLocalLogin ? (
            <button
              onClick={() => setShowLocalLogin(true)}
              className="w-full text-center text-gray-600 hover:text-primary text-sm font-medium py-2 hover:underline transition-colors"
            >
              Lokaler Admin-Login
            </button>
          ) : (
            <form onSubmit={handleLocalLogin} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-charcoal mb-1">
                  Benutzername
                </label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="input"
                  placeholder="Benutzername"
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-charcoal mb-1">
                  Passwort
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input"
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-secondary w-full py-3 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <KeyRound className="w-5 h-5" />
                )}
                <span>Anmelden</span>
              </button>

              <button
                type="button"
                onClick={() => setShowLocalLogin(false)}
                className="w-full text-center text-gray-500 hover:text-gray-700 text-sm mt-2 transition-colors"
              >
                Zurück zu Nextcloud-Login
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-gray-400 text-sm mt-10">
          {instance.name.long}
        </p>
      </div>
    </div>
  );
}
